// The preparation report of an image version: every step the boot server took to make the disk bootable, and what each
// changed. Read leniently (`reportSteps`): a step the server words differently still shows its name.

import { useTranslation } from '@clavian/i18n/react';
import { Badge } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { reportSteps } from './format';

const TONE = { ok: 'ok', failed: 'danger', skipped: 'neutral', unknown: 'neutral' } as const;

export function PreparationReport({ report }: { readonly report: Record<string, unknown> | null | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const steps = reportSteps(report);
  if (steps.length === 0) return <p className="console-muted">{t('boot.report.none')}</p>;
  return (
    <ol className="console-boot__report" aria-label={t('boot.report.label')}>
      {steps.map((step, index) => (
        <li key={`${index}:${step.name}`} className="console-boot__report-step">
          <div className="console-boot__report-head">
            <span className="console-boot__report-name">{step.name}</span>
            <Badge tone={TONE[step.status]} dot>
              {t(`boot.report.status.${step.status}`)}
            </Badge>
          </div>
          {step.details.map((detail, line) => (
            <p key={`d${line}`} className="console-muted">
              {detail}
            </p>
          ))}
          {step.changes.length === 0 ? (
            <p className="console-muted">{t('boot.report.noChanges')}</p>
          ) : (
            <ul className="console-boot__report-changes">
              {step.changes.map((change, line) => (
                <li key={`c${line}`}>{change}</li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}
